import {useCallback, useContext, useEffect, useRef, useState} from 'react';
import {createPortal} from 'react-dom';
import {useDispatch, useSelector} from 'react-redux';
import {EventCreateContext} from '~/common/FabricInvitation/EventCreateDesign/EventCreateContext';
import {UPDATE_DESIGN_OPTIONS} from '~/common/fabric/reducers/fabricDesign/constants';
import {selectTextAreaOpen} from '~/common/fabric/reducers/fabricDesign/selectors';
import {Button} from '~library/atoms/Button';
import {useMatchQuery} from '~library/hooks/useMatchQuery';
import {largishAndUp} from '~libSass/base/_exports.sass';
import {TextEditorTooltip} from './TextEditorTooltip';
import './TextEditorSmall.sass';

const letterCasingToTextTransform = {
  upper: 'uppercase',
  lower: 'lowercase',
  capitalize: 'capitalize',
};

export const TextEditorSmall = () => {
  const {canvas, activeObjects} = useContext(EventCreateContext);
  const textAreaRef = useRef(null);
  const [text, setText] = useState('');
  const [originalText, setOriginalText] = useState('');
  const [fontFamily, setFontFamily] = useState('');
  const [fontWeight, setFontWeight] = useState('');
  const [textAlign, setTextAlign] = useState('');
  const [letterCasing, setLetterCasing] = useState(null);
  const [isTooltipVisible, setIsTooltipVisible] = useState(true);
  const [heightDifference, setHeightDifference] = useState(0);
  const textAreaOpen = useSelector(selectTextAreaOpen);
  const dispatch = useDispatch();
  const {matches: isDesktop} = useMatchQuery(`(min-width: ${largishAndUp})`);

  const getTextTransform = () => letterCasingToTextTransform[letterCasing] || 'none';

  // biome-ignore lint/correctness/useExhaustiveDependencies: deps
  useEffect(() => {
    if (textAreaOpen && activeObjects?.length > 0) {
      const [activeObject] = activeObjects;
      setFontFamily(activeObject?.fontFamily);
      setFontWeight(activeObject?.fontWeight);
      setTextAlign(activeObject?.textAlign);
      setLetterCasing(activeObject?.letterCasing);
      setText(activeObject?.text);
      setOriginalText(activeObject?.text);
    }
  }, [canvas, textAreaOpen]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: deps
  useEffect(() => {
    const onTextEditingEntered = () =>
      dispatch({
        type: UPDATE_DESIGN_OPTIONS,
        payload: {textAreaOpen: true},
      });

    canvas?.on({
      'text:editing:entered': onTextEditingEntered,
    });

    return () => {
      canvas?.off({
        'text:editing:entered': onTextEditingEntered,
      });
    };
  }, [canvas]);

  useEffect(() => {
    const detectKeyboard = () => {
      if (window.visualViewport) {
        const viewportHandler = () => {
          setHeightDifference(window.innerHeight - window.visualViewport.height);
        };

        window.visualViewport.addEventListener('resize', viewportHandler);
        return () => window.visualViewport.removeEventListener('resize', viewportHandler);
      }

      return () => {
        setHeightDifference(0);
      };
    };

    return detectKeyboard();
  }, []);

  useEffect(() => {
    const hiddenTextarea = document.querySelector('textarea[data-fabric-hiddentextarea]');

    if (textAreaOpen && hiddenTextarea && !isDesktop) {
      hiddenTextarea.style.display = 'none';
    }

    return () => {
      if (hiddenTextarea) {
        hiddenTextarea.style.display = '';
      }
    };
  }, [textAreaOpen, isDesktop]);

  const changeText = useCallback(
    (newText) => {
      const [activeObject] = activeObjects || [];
      if (activeObject) {
        setText(newText);
        activeObject.text = newText;
      }
    },
    [activeObjects]
  );

  const onTextChangeHandler = useCallback(({target: {value}}) => changeText(value), [changeText]);

  const closeEditor = () => {
    const activeObject = canvas?.getActiveObject();
    activeObject?.exitEditing?.();
    dispatch({
      type: UPDATE_DESIGN_OPTIONS,
      payload: {textAreaOpen: false},
    });
    canvas.requestRenderAll();
  };

  const onCancelClickHandler = () => {
    changeText(originalText);
    closeEditor();
  };

  const onClearClickHandler = () => {
    changeText('');
  };

  const onDoneClickHandler = () => {
    changeText(text);
    closeEditor();
  };

  if (isDesktop || !textAreaOpen) {
    return null;
  }

  return createPortal(
    <div className="text-editor-mw-small">
      <div className="text-editor-mw-small__header">
        <Button
          data-qa-id="step1-texteditor-close"
          className="text-editor-mw-small__header__button text-editor-mw-small__button-cancel"
          variant="secondary"
          size="medium"
          onClick={onCancelClickHandler}
        >
          Cancel
        </Button>
        <Button
          data-qa-id="step1-texteditor-close"
          variant="tertiary"
          size="medium"
          onClick={onClearClickHandler}
        >
          Clear
        </Button>
        <Button
          data-qa-id="step1-texteditor-close"
          className="text-editor-mw-small__header__button text-editor-mw-small__button-done"
          variant="primary"
          size="medium"
          onClick={onDoneClickHandler}
        >
          Done
        </Button>
      </div>
      <div className="text-editor-mw-small__body">
        <div className="text-editor-mw-small__body__content">
          <textarea
            ref={textAreaRef}
            style={{
              fontFamily,
              fontWeight,
              textAlign,
              textTransform: getTextTransform(),
            }}
            onChange={onTextChangeHandler}
            value={text}
            key={originalText}
            data-qa-id="step1-texteditor-input"
            placeholder="Enter your text here..."
            className="text-editor-mw-small__body__content__textarea"
          />
        </div>
      </div>
      <div className="text-editor-mw-small__tooltip" style={{bottom: `${heightDifference}px`}}>
        <TextEditorTooltip
          isVisible={isTooltipVisible}
          onClose={() => setIsTooltipVisible(false)}
        />
      </div>
    </div>,
    document.getElementById('pbModalRoot')
  );
};
